import React, { useState } from "react";
import {
    Save,
    Newspaper,
    Calendar,
    Link as LinkIcon,
    Image as ImageIcon,
    Info
} from "lucide-react";

export default function PressForm({ initialData = {}, onSubmit, isEditing = false }) {
    const [formData, setFormData] = useState({
        publication: initialData.publication || "",
        date: initialData.date || "",
        headline: initialData.headline || "",
        excerpt: initialData.excerpt || "",
        type: initialData.type || "feature",
        logo: initialData.logo || "",
        link: initialData.link || "",
        status: initialData.status || "active",
        ...initialData
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Primary Content */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "Playfair Display, serif" }}>
                            <Newspaper size={20} className="text-jazz-brown" /> Coverage Details
                        </h2>

                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Publication Name</label>
                                    <input
                                        type="text"
                                        name="publication"
                                        value={formData.publication}
                                        onChange={handleChange}
                                        placeholder="e.g., Jazz Times Magazine"
                                        className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Display Date</label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-black/20" size={16} />
                                        <input
                                            type="text"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleChange}
                                            placeholder="e.g., January 2026"
                                            className="w-full pl-10 pr-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Headline</label>
                                <input
                                    type="text"
                                    name="headline"
                                    value={formData.headline}
                                    onChange={handleChange}
                                    placeholder="e.g., The Voice of a New Jazz Era"
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Excerpt</label>
                                <textarea
                                    name="excerpt"
                                    value={formData.excerpt}
                                    onChange={handleChange}
                                    rows={4}
                                    placeholder="An overview or a captivating sentence from the article..."
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium resize-none"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "Playfair Display, serif" }}>
                            <LinkIcon size={20} className="text-jazz-brown" /> Media & Links
                        </h2>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Publication Logo URL</label>
                                <div className="relative">
                                    <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-black/20" size={16} />
                                    <input
                                        type="url"
                                        name="logo"
                                        value={formData.logo}
                                        onChange={handleChange}
                                        placeholder="https://..."
                                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Full Article Link</label>
                                <div className="relative">
                                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-black/20" size={16} />
                                    <input
                                        type="url"
                                        name="link"
                                        value={formData.link}
                                        onChange={handleChange}
                                        placeholder="https://..."
                                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: classification */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <h3 className="text-lg font-bold mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                            Classification
                        </h3>
                        <div className="space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Article Type</label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-bold tracking-widest uppercase cursor-pointer"
                                >
                                    <option value="feature">FEATURE</option>
                                    <option value="review">REVIEW</option>
                                    <option value="interview">INTERVIEW</option>
                                    <option value="video">VIDEO CONTENT</option>
                                    <option value="award">AWARD / LIST</option>
                                </select>
                            </div>

                            <div className="flex items-center justify-between p-4 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222]">
                                <div>
                                    <p className="text-xs font-bold text-black uppercase tracking-widest">Active Status</p>
                                    <p className="text-xs text-black dark:text-white/50 uppercase tracking-widest mt-0.5">Visible in Press List</p>
                                </div>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="bg-transparent text-xs font-bold uppercase tracking-widest focus:outline-none cursor-pointer"
                                >
                                    <option value="active">ACTIVE</option>
                                    <option value="disabled">DISABLED</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="bg-black text-white p-6 lg:p-8 rounded-2xl shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-jazz-brown/20 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
                        <h3 className="text-lg font-bold mb-4 relative z-10" style={{ fontFamily: "Playfair Display, serif" }}>
                            Curator Advice
                        </h3>
                        <div className="flex gap-3 relative z-10">
                            <Info size={16} className="text-jazz-brown flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-white leading-relaxed uppercase tracking-widest">
                                Keep headlines punchy and excerpts short to maintain the minimalist aesthetic of the main press page.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <button
                            type="submit"
                            className="w-full flex items-center justify-center gap-2 bg-black text-white py-4 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all shadow-xl shadow-black/10"
                        >
                            <Save size={18} /> {isEditing ? "UPDATE COVERAGE" : "PUBLISH COVERAGE"}
                        </button>
                        <button
                            type="button"
                            onClick={() => window.history.back()}
                            className="w-full py-4 rounded-xl text-xs font-bold tracking-widest text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white border border-black/5 bg-white dark:bg-[#1A1A1A] transition-all"
                        >
                            CANCEL
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
}
